import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useScrollSpy } from "@/lib/useScrollSpy";
import { EVENT_FILTERS } from "@/data/content";
import { usePageMeta } from "@/lib/usePageMeta";
import { useApi } from "@/lib/api";
import { toEvent } from "@/lib/mappers";
import { isWeekendIST } from "@/lib/format";
import { SubscribeButton } from "@/components/site/SubscribeButton";
import { PageHeader, EmptyState, LoadingState, ErrorState } from "@/components/site/Content";
import { EventCard } from "@/components/site/EventCard";
import { useCopy } from "@/lib/copy";
import { Rich } from "@/components/site/Rich";


const TABS = [{ id: "week" }, { id: "upcoming" }, { id: "past" }];
const SECTION_IDS = TABS.map((t) => `events-${t.id}`);
const FILTER_LABELS = { day: "Date", domain: "Domain", type: "Type", mode: "Mode", fee: "Fee", mood: "Mood" };
const BASE_OPTIONS = { day: ["Weekdays", "Weekends"], ...EVENT_FILTERS };
const EMPTY = { day: "", domain: "", type: "", mode: "", fee: "", mood: "" };

const matches = (e, f) => {
  const weekend = isWeekendIST(e.start);
  if (f.day === "Weekdays" && weekend) return false;
  if (f.day === "Weekends" && !weekend) return false;
  if (f.domain && !e.domains.includes(f.domain)) return false;
  return ["type", "mode", "fee", "mood"].every((k) => !f[k] || e[k] === f[k]);
};

const FilterSelect = ({ name, value, onChange, options }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={`filter-${name}`} className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted">{FILTER_LABELS[name]}</label>
    <select id={`filter-${name}`} value={value} onChange={(e) => onChange(name, e.target.value)} className="field py-2.5" data-testid={`event-filter-${name}`}>
      <option value="">Any</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default function Events() {
  usePageMeta("Events", "Knowledge events across Bengaluru.");
  const t = useCopy();
  const label = (id) => t(`events.tabs.${id}`);
  const [params] = useSearchParams();
  const [filters, setFilters] = useState(EMPTY);
  const active = Object.values(filters).some(Boolean);
  const barRef = useRef(null);
  const [current, scrollToSection] = useScrollSpy(SECTION_IDS, barRef);

  const { data, loading, error } = useApi("/events/sections");
  const { data: cats } = useApi("/categories");
  const options = { ...BASE_OPTIONS, domain: cats?.researchDomain || [], type: cats?.eventType || [] };

  const all = useMemo(() => {
    const out = {};
    TABS.forEach((t) => {
      out[t.id] = (data?.[t.id] || []).map(toEvent);
    });
    return out;
  }, [data]);
  const groups = useMemo(() => {
    const out = {};
    TABS.forEach((t) => {
      out[t.id] = all[t.id].filter((e) => matches(e, filters));
    });
    return out;
  }, [all, filters]);
  const total = TABS.reduce((n, t) => n + groups[t.id].length, 0);

  useEffect(() => {
    const when = params.get("when");
    if (!loading && when && when !== "week" && TABS.some((t) => t.id === when)) setTimeout(() => scrollToSection(`events-${when}`), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const onTab = (e, id) => {
    e.preventDefault();
    scrollToSection(id);
  };

  return (
    <>
      <PageHeader eyebrow={t("events.header.eyebrow")} title={t("events.header.title")} intro={t("events.header.intro") && <Rich text={t("events.header.intro")} />}>
        <SubscribeButton label={t("events.header.subscribe")} href={t("contact.social.prasaraUrl")} note={t("events.header.nudge")} testId="events-subscribe-button" />
      </PageHeader>

      <section className="container-x py-10 md:py-14" aria-label="Event list">
        <nav ref={barRef} aria-label="Event sections" className="sticky top-20 z-30 -mx-4 border-b border-line bg-white/95 px-4 backdrop-blur-md sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12" data-testid="events-tab-bar">
          <ul className="flex gap-1">
            {TABS.map((tab) => {
              const id = `events-${tab.id}`;
              const on = current === id;
              return (
                <li key={tab.id}>
                  <a href={`#${id}`} aria-current={on ? "true" : undefined} onClick={(e) => onTab(e, id)} className={`tab-btn inline-block ${on ? "tab-btn-active" : ""}`} data-testid={`events-tab-${tab.id}`}>
                    {label(tab.id)}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <fieldset className="mt-8 border border-line bg-mist p-5 md:p-6" data-testid="event-filters">
          <legend className="sr-only">Filter events</legend>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {Object.keys(EMPTY).map((k) => <FilterSelect key={k} name={k} value={filters[k]} onChange={setFilter} options={options[k]} />)}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-muted" aria-live="polite" data-testid="event-results-count">
              {total} {total === 1 ? "event" : "events"} across all sections
            </p>
            {active && <button type="button" onClick={() => setFilters(EMPTY)} className="text-sm font-bold text-brand underline-offset-4 hover:underline" data-testid="event-reset-filters">Reset filters</button>}
          </div>
        </fieldset>

        {TABS.map((tab) => {
          const list = groups[tab.id];
          return (
            <section key={tab.id} id={`events-${tab.id}`} aria-labelledby={`events-${tab.id}-heading`} className="scroll-mt-[150px] pt-12" data-testid={`events-section-${tab.id}`}>
              <h2 id={`events-${tab.id}-heading`} tabIndex={-1} className="mb-6 flex items-baseline gap-3 text-2xl font-extrabold tracking-tight text-brand focus:outline-none sm:text-3xl">
                {label(tab.id)}
                <span className="text-sm font-semibold text-ink-muted" data-testid={`events-section-${tab.id}-count`}>
                  {list.length} {list.length === 1 ? "event" : "events"}
                </span>
              </h2>
              {loading ? (
                <LoadingState className="h-48" label={`Loading ${label(tab.id)} events`} />
              ) : error ? (
                <ErrorState testId={`event-error-${tab.id}`} />
              ) : list.length ? (
                <div className="grid gap-5 md:grid-cols-2">{list.map((e) => <EventCard key={e.slug} event={e} />)}</div>
              ) : (
                <EmptyState title={t("events.list.emptyTitle")} message={active ? "No events in this section match these filters." : t("events.list.emptyText")} testId={`event-empty-state-${tab.id}`} />
              )}
            </section>
          );
        })}
      </section>
    </>
  );
}
